import { WORD_MASTER_WORDS } from '../../data/wordList';

const WORD_LENGTH = 5;
const MAX_GUESSES = 6;

/**
 * Get the daily puzzle word based on date (deterministic, same for everyone)
 */
const PUZZLE_EPOCH = new Date('2024-01-01').getTime();

/** Date string for localStorage / daily tracking (YYYY-MM-DD) */
export function getDateKey(d = new Date()) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/**
 * Get puzzle number (days since epoch) for share format
 */
export function getPuzzleNumber() {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.floor((today.getTime() - PUZZLE_EPOCH) / 86400000);
}

export function getDailyWord() {
  const today = new Date();
  const dateStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
  let hash = 0;
  for (let i = 0; i < dateStr.length; i++) {
    const char = dateStr.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }
  const index = Math.abs(hash) % WORD_MASTER_WORDS.length;
  return WORD_MASTER_WORDS[index];
}

/** Random word for practice/free play mode */
export function getRandomWord() {
  const index = Math.floor(Math.random() * WORD_MASTER_WORDS.length);
  return WORD_MASTER_WORDS[index];
}

/** Lazy-loaded — `validGuesses.js` is huge (~15k words, one line); keep it out of the main bundle parse path. */
let validGuessesModulePromise = null;

function loadValidGuessesModule() {
  if (!validGuessesModulePromise) {
    validGuessesModulePromise = import('../../data/validGuesses');
  }
  return validGuessesModulePromise;
}

/** Warm the valid-guess dictionary (call from game mount so the first submit is instant). */
export function preloadValidGuesses() {
  return loadValidGuessesModule();
}

/**
 * Validate guess — must be 5 letters and in valid guesses list (~14,800 words).
 * Async so the dictionary can load in a separate chunk without freezing the tab.
 */
export async function isValidGuessAsync(guess) {
  const { VALID_GUESSES } = await loadValidGuessesModule();
  const normalized = guess.toUpperCase().trim();
  if (normalized.length !== WORD_LENGTH) return false;
  return VALID_GUESSES.has(normalized);
}

/**
 * Evaluate guess against target word
 * Returns array of: 'correct' | 'present' | 'absent'
 */
export function evaluateGuess(guess, target) {
  const result = new Array(WORD_LENGTH).fill('absent');
  const targetChars = [...target.toUpperCase()];
  const guessChars = [...guess.toUpperCase()];

  // First pass: mark correct positions
  for (let i = 0; i < WORD_LENGTH; i++) {
    if (guessChars[i] === targetChars[i]) {
      result[i] = 'correct';
      targetChars[i] = null; // Mark as used
    }
  }

  // Second pass: mark present (wrong position)
  for (let i = 0; i < WORD_LENGTH; i++) {
    if (result[i] === 'correct') continue;
    const idx = targetChars.indexOf(guessChars[i]);
    if (idx !== -1) {
      result[i] = 'present';
      targetChars[idx] = null;
    }
  }

  return result;
}

export { WORD_LENGTH, MAX_GUESSES };
